'use client';

import { useId } from 'react';
import { BlockRoot } from '@akademi/site-builder-runtime';
import { Icon } from '../shared/Icon';
import {
  headingLevel,
  items,
  SkLink,
  surfaceClass,
  text,
  type HeadingLevel,
  type Localized,
  type Surface,
} from '../shared/primitives';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';

/**
 * "Popular colours": a row of named swatches, each a circle with its label.
 *
 * <p>The colour name is always visible text, never conveyed by colour alone. A
 * swatch with an `href` becomes a link to that colour's range or finish page.
 * The row scrolls horizontally on narrow screens rather than wrapping into an
 * uneven grid; the list stays a real list for assistive technology.
 */
export interface SpatialKitchenSwatch {
  name?: Localized;
  /** `#RRGGBB`. */
  hex?: string;
  /** Optional finish note under the name, e.g. "Matt lacquer". */
  note?: Localized;
  href?: string;
}

export interface SpatialKitchenColourSwatchesProps {
  eyebrow?: Localized;
  title?: Localized;
  intro?: Localized;
  swatches?: SpatialKitchenSwatch[];
  linkLabel?: Localized;
  linkHref?: string;
  surface?: Surface;
  headingLevel?: HeadingLevel;
}

const HEX = /^#[0-9a-f]{6}$/i;

const CSS = `
.sk-swatches__head{display:flex;flex-wrap:wrap;align-items:end;justify-content:space-between;gap:1rem 2rem;margin-bottom:clamp(2rem,1.5rem + 2vw,3rem)}
.sk-swatches__head .sk-section-head{margin-bottom:0}
.sk-swatches__list{display:flex;gap:clamp(1.25rem,2.5vw,2.5rem);overflow-x:auto;padding:.25rem .25rem 1rem;scroll-snap-type:x proximity}
.sk-swatch{flex:0 0 auto;width:7.5rem;scroll-snap-align:start;text-align:center}
.sk-swatch a{color:inherit;text-decoration:none;display:grid;justify-items:center;gap:.75rem}
.sk-swatch a:hover .sk-swatch__name{text-decoration:underline;text-underline-offset:.3em}
.sk-swatch__chip{display:block;width:clamp(4.5rem,8vw,6rem);aspect-ratio:1;border-radius:999px;border:1px solid rgba(39,45,49,.14);box-shadow:inset 0 -6px 14px rgba(0,0,0,.06)}
.sk-swatch__plain{display:grid;justify-items:center;gap:.75rem}
.sk-swatch__name{font-weight:500;font-size:.9375rem;color:var(--sk-ink)}
.sk-swatch__note{display:block;font-size:.8125rem;color:var(--sk-muted);margin-top:-.5rem}
@media (min-width:1100px){.sk-swatches__list{justify-content:space-between;overflow:visible}}
`;

export function SpatialKitchenColourSwatches(props: SpatialKitchenColourSwatchesProps) {
  const swatches = items<SpatialKitchenSwatch>(props.swatches);
  const title = text(props.title, 'Popular colours');
  const level = headingLevel(props.headingLevel, 'h2');
  const Title = level;
  const linkLabel = text(props.linkLabel);
  const titleId = useId();

  return (
    <BlockRoot
      as="section"
      className={`sk sk-swatches sk-section ${surfaceClass(props.surface, 'warm')}`}
      aria-labelledby={titleId}
    >
      <SkBaseStyles />
      <SkStyles id="colour-swatches" css={CSS} />
      <div className="sk-container">
        <div className="sk-swatches__head">
          <div className="sk-section-head">
            {text(props.eyebrow) && <p className="sk-eyebrow">{text(props.eyebrow)}</p>}
            <Title id={titleId} className="sk-heading sk-h2">
              {title}
            </Title>
            {text(props.intro) && <p className="sk-lead">{text(props.intro)}</p>}
          </div>
          {linkLabel && props.linkHref && (
            <SkLink href={props.linkHref} className="sk-textlink">
              {linkLabel}
              <Icon name="arrow-right" size={18} />
            </SkLink>
          )}
        </div>
        <ul
          className="sk-swatches__list sk-list-reset"
          // A scrolling region with no links inside needs its own tab stop,
          // or keyboard users cannot reach the swatches that overflow.
          {...(swatches.some((swatch) => swatch.href) ? {} : { tabIndex: 0, 'aria-labelledby': titleId })}
        >
          {swatches.map((swatch, index) => {
            const name = text(swatch.name);
            const note = text(swatch.note);
            const hex = swatch.hex && HEX.test(swatch.hex) ? swatch.hex : '#D3CDBF';
            const inner = (
              <>
                <span className="sk-swatch__chip" style={{ background: hex }} aria-hidden="true" />
                <span className="sk-swatch__name">{name}</span>
                {note && <span className="sk-swatch__note">{note}</span>}
              </>
            );
            return (
              <li key={`${name}-${index}`} className="sk-swatch">
                {swatch.href ? <SkLink href={swatch.href}>{inner}</SkLink> : <div className="sk-swatch__plain">{inner}</div>}
              </li>
            );
          })}
        </ul>
      </div>
    </BlockRoot>
  );
}
